import React from "react";
import { Editor } from "@tinymce/tinymce-react";
import { UseFormReturnType } from "@mantine/form";
import { FAQSubmit } from "../../model/FAQModel";

type Props = {
  form: UseFormReturnType<FAQSubmit>;
  index: number;
  itemId: string | number;
  registerEditor?: (id: string | number, editor: any) => void;
  externalContent?: string;
  editorHeight?: number;
};

const QuestionEditor = React.memo(function QuestionEditor({
  form,
  index,
  itemId,
  registerEditor,
  externalContent,
  editorHeight = 360,
}: Props) {
  const editorRef = React.useRef<any>(null);
  const debounceRef = React.useRef<number | null>(null);
  const initialContentRef = React.useRef(externalContent);
  const previousItemIdRef = React.useRef(itemId);

  // -----------------------
  // INIT: Set content khi editor được khởi tạo
  // -----------------------
  const handleInit = (_evt: any, editor: any) => {
    editorRef.current = editor;
    editor.setContent(externalContent ?? "");
    registerEditor?.(itemId, editor);
  };

  // -----------------------
  // Chỉ sync khi itemId thay đổi (chuyển FAQ khác)
  // KHÔNG sync khi externalContent thay đổi do user gõ
  // -----------------------
  React.useEffect(() => {
    if (previousItemIdRef.current !== itemId) {
      previousItemIdRef.current = itemId;
      initialContentRef.current = externalContent;

      if (
        editorRef.current &&
        typeof editorRef.current.setContent === "function"
      ) {
        editorRef.current.setContent(externalContent ?? "");
      }
    }
  }, [itemId, externalContent]);

  // -----------------------
  // Editor change (debounce)
  // -----------------------
  const handleEditorChange = (content: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    debounceRef.current = window.setTimeout(() => {
      form.setFieldValue(`dataSubmit.${index}.answer`, content || "");
    }, 250) as unknown as number;
  };

  // -----------------------
  // Blur → sync ngay
  // -----------------------
  const handleBlur = () => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const val = editorRef.current?.getContent() || "";
    form.setFieldValue(`dataSubmit.${index}.answer`, val);
  };

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <Editor
      ref={editorRef}
      onInit={handleInit}
      initialValue={initialContentRef.current}
      onEditorChange={(c: any, editorApi: any) =>
        handleEditorChange(editorApi?.getContent() || c)
      }
      onBlur={handleBlur}
      init={{
        height: 350,
        menubar: false,
        plugins: ["lists", "link", "autolink", "paste", "quickbars"],
        toolbar:
          "bold italic underline | bullist numlist | link unlink | removeformat",
        toolbar_mode: "floating",
        valid_elements: "*[*]",
        element_format: "html",
        paste_as_text: false,
      }}
    />
  );
});

export default QuestionEditor;
